/** The pre-trip check and the odometer of a route (spec F-DRV-2, rule D-11). */

export const DAY_MILES = 2000;
export const ITEMS_MAX = 30;
export const LABEL_MAX = 80;

export interface CheckItem {
  id: string;
  label: string;
}

export interface CheckAnswer {
  ok: boolean | null;
  note: string;
}

export interface CheckResult {
  item_id: string;
  label: string;
  ok: boolean;
  note: string;
}

const miles = (value: number) => value.toLocaleString("en-US");

/** The odometer as a whole number of miles, or null when the text is not one. */
export function parseOdometer(text: string): number | null {
  const clean = text.replace(/[,\s]/g, "");
  if (!/^\d{1,7}$/.test(clean)) return null;
  const value = Number(clean);
  return value <= 2_000_000 ? value : null;
}

/** What stops the start, or null: each item answered, a note on each item that is not OK. */
export function startProblem(items: readonly CheckItem[], answers: Readonly<Record<string, CheckAnswer>>, odometer: string, last: number | null): string | null {
  const open = items.filter((item) => answers[item.id]?.ok == null);
  if (open.length) return `Answer each item: ${open.map((item) => item.label).join(", ")}.`;
  const bare = items.find((item) => answers[item.id]?.ok === false && !answers[item.id].note.trim());
  if (bare) return `Tell what is wrong with ${bare.label}.`;
  const value = parseOdometer(odometer);
  if (value === null) return "Enter the odometer in whole miles.";
  if (last !== null && value < last) return `The last reading of this truck is ${miles(last)} miles.`;
  return null;
}

export const SAME_MILES = "The end odometer is the same as the start: 0 miles. Check the odometer, or select Finish route with 0 miles.";

export function finishProblem(start: number | null, odometer: string): string | null {
  const value = parseOdometer(odometer);
  if (value === null) return "Enter the odometer in whole miles.";
  if (start === null) return "This route has no start odometer.";
  if (value < start) return `The end is below the start (${miles(start)} miles).`;
  if (value - start > DAY_MILES) return `A route is ${miles(DAY_MILES)} miles at most.`;
  return null;
}

export function failedItems(results: readonly CheckResult[]): CheckResult[] {
  return results.filter((result) => !result.ok);
}

/** One line on the check of a route, for the driver and the dispatcher. */
export function checkText(check: { odometer: number; results: readonly CheckResult[] } | null, end: number | null): string {
  if (!check) return "No pre-trip check.";
  const bad = failedItems(check.results).length;
  const items = check.results.length === 0 ? "no checklist" : bad === 0 ? "all items OK" : `${bad} of ${check.results.length} items not OK`;
  const trip = end === null ? `start ${miles(check.odometer)} mi` : `${miles(check.odometer)}–${miles(end)} mi (${miles(end - check.odometer)} mi)`;
  return `Checked: ${items} · ${trip}`;
}

/** The checklist rows to save: blank rows go, the order stays. */
export function cleanItems(rows: readonly { id: string | null; label: string }[]): { id: string | null; label: string }[] {
  return rows.map((row) => ({ id: row.id, label: row.label.trim() })).filter((row) => row.label !== "");
}

export function firstBlank<T extends { label: string }>(rows: readonly T[]): T | null {
  return rows.find((row) => row.label.trim() === "") ?? null;
}

export function blankProblem(rows: readonly { label: string }[]): string | null {
  return rows.some((row) => row.label.trim() === "") ? "Give a name for each item, or remove the empty row." : null;
}

export function labelLengthText(label: string): string | null {
  const length = label.trim().length;
  return length > LABEL_MAX ? `An item name is ${LABEL_MAX} characters at most (now ${length}).` : null;
}

export function itemsProblem(rows: readonly { label: string }[]): string | null {
  if (rows.length > ITEMS_MAX) return `A checklist has ${ITEMS_MAX} items at most.`;
  const long = rows.find((row) => row.label.length > LABEL_MAX);
  if (long) return `An item name has ${LABEL_MAX} characters at most.`;
  const seen = new Set<string>();
  for (const row of rows) {
    const key = row.label.toLowerCase();
    if (seen.has(key)) return `The item ${row.label} is on the list two times.`;
    seen.add(key);
  }
  return null;
}
